/**
 * Folder-aware file picking.
 *
 * Browsers expose a picked or dropped folder two different ways: a
 * `webkitdirectory` input yields a flat FileList whose entries carry
 * `webkitRelativePath`, while a drag-and-drop gives `DataTransferItem`s
 * that must be walked through the File and Directory Entries API. Both
 * are normalised here into files tagged with their directory segments so
 * callers can either mirror the tree or flatten it.
 */

export interface PickedFile {
  file: File;
  /**
   * Directory segments relative to what was picked, including the picked
   * folder's own name (`["Logos", "Dark"]` for `Logos/Dark/mark.svg`).
   * Empty for loose files.
   */
  dirs: string[];
}

/** Deepest tree the admin drive allows; matches tg_admin_folders_validate. */
export const MAX_FOLDER_DEPTH = 25;

const JUNK_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', 'ehthumbs.db']);
const JUNK_DIRS = new Set(['__macosx', 'node_modules']);

/** OS/sidecar files that nobody means to upload when they grab a folder. */
export function isJunkFile(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.startsWith('.') || JUNK_FILES.has(lower);
}

function isJunkDir(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.startsWith('.') || JUNK_DIRS.has(lower);
}

function cleanDirs(segments: string[]): string[] | null {
  const dirs = segments.map((segment) => segment.trim()).filter(Boolean);
  if (dirs.some(isJunkDir)) return null;
  return dirs;
}

/**
 * Files from an `<input type="file">`. Works for plain multi-select (no
 * dirs) and `webkitdirectory` picks (dirs from `webkitRelativePath`).
 */
export function pickedFromFileList(list: FileList | File[]): PickedFile[] {
  const out: PickedFile[] = [];
  for (const file of Array.from(list)) {
    if (isJunkFile(file.name)) continue;
    const rel = file.webkitRelativePath || '';
    const segments = rel ? rel.split('/').slice(0, -1) : [];
    const dirs = cleanDirs(segments);
    if (!dirs) continue;
    out.push({ file, dirs });
  }
  return out;
}

/**
 * Files from a drop, descending into any dropped folders. Must be called
 * synchronously from the drop handler: the browser empties `dataTransfer`
 * once the event returns, so entries are captured before the first await.
 */
export function pickedFromDataTransfer(transfer: DataTransfer): Promise<PickedFile[]> {
  const items = Array.from(transfer.items ?? []);
  const entries = items
    .filter((item) => item.kind === 'file')
    .map((item) => (typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null));

  if (!entries.length || entries.some((entry) => entry === null)) {
    return Promise.resolve(pickedFromFileList(transfer.files));
  }

  return (async () => {
    const out: PickedFile[] = [];
    for (const entry of entries) {
      if (entry) await walkEntry(entry, [], out);
    }
    return out;
  })();
}

async function walkEntry(entry: FileSystemEntry, dirs: string[], out: PickedFile[]) {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) =>
      (entry as FileSystemFileEntry).file(resolve, reject),
    );
    if (!isJunkFile(file.name)) out.push({ file, dirs });
    return;
  }
  if (!entry.isDirectory) return;
  if (isJunkDir(entry.name)) return;
  const next = [...dirs, entry.name.trim()].filter(Boolean);
  if (next.length > MAX_FOLDER_DEPTH) return;

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries returns results in batches (Chrome caps at 100) and
  // signals completion with an empty batch.
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
      reader.readEntries(resolve, reject),
    );
    if (!batch.length) break;
    for (const child of batch) await walkEntry(child, next, out);
  }
}

/** Drop the directory structure and just return the files. */
export function flattenPicked(picked: PickedFile[]): File[] {
  return picked.map((item) => item.file);
}

/**
 * Mirrors the browser's `accept` matching for files that arrived through
 * a folder pick, where `accept` can't be applied by the picker itself.
 * Tokens are `.ext`, `type/subtype`, or `type/*`.
 */
export function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;
  const tokens = accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return type.startsWith(token.slice(0, -1));
    return type === token;
  });
}

/** Distinct directory paths present in a pick, shallowest first. */
export function pickedDirectories(picked: PickedFile[]): string[][] {
  const seen = new Map<string, string[]>();
  for (const item of picked) {
    for (let depth = 1; depth <= item.dirs.length; depth += 1) {
      const path = item.dirs.slice(0, depth);
      const key = path.map((segment) => segment.toLowerCase()).join('\u0000');
      if (!seen.has(key)) seen.set(key, path);
    }
  }
  return [...seen.values()].sort((a, b) => a.length - b.length);
}

/**
 * Props for a hidden folder-picker input. React's typings don't know
 * `webkitdirectory`, and the attribute is boolean-by-presence, so it is
 * passed through as a spread.
 */
export const FOLDER_INPUT_PROPS = { webkitdirectory: '', directory: '' } as const;
